import { WithdrawData, HotWalletWithdrawData } from "@/types";
import { useMutation, useQuery } from "@tanstack/react-query";
import { backendApi } from "./api.service";

const config = {
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
};

// Daily user withdrawal
export const useWithdraw = () => {
  const config = {
    withCredentials: true,
    headers: {
      "Content-Type": "application/json",
    },
  };

  return useMutation({
    mutationFn: (withdrawData: WithdrawData) =>
      backendApi.post("/withdraw/daily-withdraw", withdrawData, config),
  });
};

// Hot wallet withdrawal
export const useHotWalletWithdraw = () => {
  return useMutation({
    mutationFn: (withdrawData: HotWalletWithdrawData) =>
      backendApi.post(`/admin/hotwallets/from-hotwallet`, withdrawData, config),
  });
};

// Get all hot wallets
export const useGetHotWallets = () => {
  return useQuery({
    queryKey: ["hotWallets"],
    queryFn: () => backendApi.get(`/admin/hotwallets/hot-wallets`, config),
  });
};

// Create new hot wallet
export const useCreateHotWallet = () => {
  return useMutation({
    mutationFn: () =>
      backendApi.post(`/admin/hotwallets/create-wallet`, {}, config),
  });
};

// Get user withdrawal history
export const useGetWithdrawalHistory = (
  page = 1,
  pageSize = 10,
  hash?: string,
) => {
  return useQuery({
    queryKey: ["withdrawalHistory", page, pageSize, hash],
    queryFn: () =>
      backendApi.get(`/withdraw/history`, {
        ...config,
        params: {
          page,
          pageSize,
          ...(hash && { hash }),
        },
      }),
  });
};

// Get user's withdrawal address for specific chain
export const useGetWithdrawAddress = (chain: string) => {
  return useQuery({
    queryKey: ["withdrawAddress", chain],
    queryFn: () => backendApi.get(`/withdraw/address/${chain}`, config),
  });
};

// Get user's withdrawal detail
export const useGetTransactionDetail = (id: string) => {
  return useQuery({
    queryKey: ["transactionDetail", id],
    queryFn: () => backendApi.get(`/transactions/${id}`, config),
    retry: false,
  });
};

interface UpdateTransactionStatusBody {
  status: string;
  note: string;
  txHash: string;
}
// Update transaction status
export const useUpdateTransactionStatus = (id: string) => {
  return useMutation({
    mutationFn: (data: UpdateTransactionStatusBody) =>
      backendApi.patch(`/transactions/${id}`, data),
    retry: false,
  });
};

// Example usage in components:
/*
  // For daily withdrawal
  const { mutate: withdraw, isLoading } = useWithdraw();
  
  const handleWithdraw = () => {
    withdraw({
      withdrawAmount: 100,
      network: "eth",
      token: "usdt",
      recipientAddress: "0x..."
    });
  };

  // For hot wallet withdrawal
  const { mutate: hotWalletWithdraw } = useHotWalletWithdraw();
  
  const handleHotWalletWithdraw = () => {
    hotWalletWithdraw({
      network: "eth",
      contract: "0x...", // or null for native token
      amount: "1000000000000000000", // 1 ETH in wei
      kmsId: "wallet-id"
    });
  };

  // To get hot wallets
  const { data: hotWallets, isLoading } = useGetHotWallets();

  // To create new hot wallet
  const { mutate: createWallet } = useCreateHotWallet();
  
  const handleCreateWallet = () => {
    createWallet();
  };

  // To get withdrawal history
  const { data: history } = useGetWithdrawalHistory(1, 10);

  // To get withdrawal address
  const { data: withdrawAddress } = useGetWithdrawAddress("eth");
*/
